import React, { useMemo } from 'react';

import { Minus, Plus } from 'lucide-react';
import { connect } from 'react-redux';

import { IdeaFieldTypes } from 'common/constants/ideas';
import { IdeaScoreFieldTypes } from 'common/constants/ideaScoreFields';
import generateRandomID from 'common/generateRandomID';
import ButtonV2 from 'common/ui/ButtonV2';
import IconButtonV2 from 'common/ui/IconButtonV2';
import SingleSelect from 'common/ui/SingleSelect';
import { P } from 'common/ui/Text';
import TextInputV2 from 'common/ui/TextInputV2';
import styles from 'css-module/components/subdomain/admin/v2/_AdminV2CustomIdeaFieldModal.module.scss';

import FormField from './FormField';

import type { IdeaField } from 'common/api/endpoints/idea';
import type { IdeasConfigurationState } from 'common/reducers/ideasConfiguration';
import type {
  ScoreChangeFields,
  ScoreFieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import type { Option } from 'common/ui/common/select/SelectCommon';

type Field = {
  tempID: string;
  fieldID: string;
  weight: number;
  type: IdeaScoreFieldTypes;
};

const hasIncompleteFields = (fields: ScoreFieldFormState['fields']) =>
  fields.some((field) => !field.fieldID);

const generateEmptyOption = (type: IdeaScoreFieldTypes): Field => ({
  tempID: generateRandomID(),
  fieldID: '',
  weight: 0,
  type,
});

const FieldInput = ({
  deletable,
  field,
  options,
  onCreateField,
  onUpdateField,
  onRemoveField,
}: {
  deletable: boolean;
  field: Field;
  options: Option[];
  onCreateField: (field: Field) => void;
  onUpdateField: (field: Field) => void;
  onRemoveField: (field: Field) => void;
}) => {
  return (
    <div className={styles.scoreField}>
      <SingleSelect
        allowClear={false}
        className={styles.fieldTypeSelect}
        onChange={(option) => {
          if (option) {
            if (!hasIncompleteFields([field])) {
              onUpdateField({ ...field, fieldID: option.value });
            } else {
              onCreateField({ ...field, fieldID: option.value });
            }
          }
        }}
        options={options}
        placeholder="Select field"
        required
        value={options.find((option) => option.value === field.fieldID)}
      />
      <TextInputV2
        className={styles.scoreFieldWeight}
        onChange={(value) => onUpdateField({ ...field, weight: Number(value) })}
        defaultValue={field.weight}
        type="number"
      />
      {deletable && (
        <IconButtonV2
          variant="plain"
          size="small"
          aria-label="Remove field"
          onClick={() => onRemoveField(field)}
          icon={Minus}
        />
      )}
    </div>
  );
};

type ConnectProps = {
  ideaFields: IdeaField[];
};

type OwnProps = {
  scoreFieldState: ScoreFieldFormState;
  setValue: (field: ScoreChangeFields, value: ScoreFieldFormState['fields']) => void;
};

type Props = ConnectProps & OwnProps;

function ScoreFieldInputs({ ideaFields, scoreFieldState: { fields }, setValue }: Props) {
  const fieldOptions = useMemo(
    () => fields.map((field) => ({ ...field, tempID: generateRandomID() })),
    [fields],
  );

  const options =
    ideaFields
      ?.filter((ideaField) =>
        [IdeaFieldTypes.formula, IdeaFieldTypes.number].includes(ideaField.type),
      )
      .map((ideaField) => ({
        label: ideaField.label,
        value: ideaField.name,
      })) || [];

  const impactFields = fieldOptions.some((field) => field.type === IdeaScoreFieldTypes.impact)
    ? fieldOptions.filter((field) => field.type === IdeaScoreFieldTypes.impact)
    : [generateEmptyOption(IdeaScoreFieldTypes.impact)];

  const costFields = fieldOptions.some((field) => field.type === IdeaScoreFieldTypes.cost)
    ? fieldOptions.filter((field) => field.type === IdeaScoreFieldTypes.cost)
    : [generateEmptyOption(IdeaScoreFieldTypes.cost)];

  const updateField = (field: Field) => {
    const updatedFields = fieldOptions.map((f) => (f.tempID === field.tempID ? field : f));
    setValue('fields', updatedFields);
  };

  const removeField = (field: Field) => {
    const updatedFields = fieldOptions.filter((f) => f.tempID !== field.tempID);
    setValue('fields', updatedFields);
  };

  const createField = (field: Field) => {
    setValue('fields', [...fieldOptions.filter((f) => f.tempID !== field.tempID), field]);
  };

  return (
    <>
      <FormField label="Impact fields">
        <P variant="bodySm" className={styles.scoreFieldSubheading}>
          Score is calculated by multiplying each field by their weight. Then the total impact is
          divided by effort. More details can be found&nbsp;
          <a
            className={styles.link}
            href="https://help.canny.io/en/articles/5046937-how-do-prioritization-factors-and-scoring-work">
            here
          </a>
          .
        </P>
        {impactFields.map((field) => (
          <FieldInput
            deletable={impactFields.length > 1}
            key={field.tempID}
            field={field}
            onUpdateField={updateField}
            onRemoveField={removeField}
            onCreateField={createField}
            options={options}
          />
        ))}
        <ButtonV2
          disabled={hasIncompleteFields(impactFields)}
          startIcon={Plus}
          variant="plain"
          size="small"
          onClick={() => createField(generateEmptyOption(IdeaScoreFieldTypes.impact))}>
          Add another field
        </ButtonV2>
      </FormField>
      <FormField label="Cost fields">
        {costFields.map((field) => (
          <FieldInput
            deletable={costFields.length > 1}
            key={field.tempID}
            field={field}
            onUpdateField={updateField}
            onRemoveField={removeField}
            onCreateField={createField}
            options={options}
          />
        ))}
        <ButtonV2
          disabled={hasIncompleteFields(costFields)}
          startIcon={Plus}
          variant="plain"
          size="small"
          onClick={() => createField(generateEmptyOption(IdeaScoreFieldTypes.cost))}>
          Add another field
        </ButtonV2>
      </FormField>
    </>
  );
}

export default connect(
  ({ ideasConfiguration }: { ideasConfiguration: IdeasConfigurationState }) => {
    const ideaFields = ideasConfiguration.data?.fields;
    return {
      ideaFields: ideaFields ?? [],
    };
  },
)(ScoreFieldInputs) as unknown as React.FC<OwnProps>;
